import { useState, useEffect, useCallback } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'
import { masterRunbooksApi } from './api'
import { Input } from '@/components/ui/input'
import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form'
import { RichTextEditor } from '@/components/ui/rich-text-editor'
import { IconPicker } from '@/features/objects/components/IconPicker'
import { RunbookTable } from '@/features/objects/components/RunbookTable'
import { GenerateCodeDialog } from './components/GenerateCodeDialog'
import { TaskScriptDialog } from './components/TaskScriptDialog'
import { Button } from '@/components/ui/button'
import { FileCode } from 'lucide-react'
import { calculateRunbookTimes } from '@/features/objects/utils/calculateRunbookTimes'
import { type MasterRunbookFormData } from './schema'
import type { RunbookTask } from '@/types/entities'

interface MasterRunbookFormProps {
    /** Existing runbook id; absent when creating, so no recommendation is possible yet. */
    masterRunbookId?: number | null
    form: UseFormReturn<MasterRunbookFormData>
    /**
     * Scripts produced by AI generation in this session, keyed by task number. They only
     * exist in memory until the runbook is saved — there is no id to fetch them by — so
     * without passing them through, a freshly generated runbook shows a command line that
     * runs a file the author cannot see.
     */
    aiCode?: Record<string, any> | null
}

export function MasterRunbookForm({ form, masterRunbookId = null, aiCode = null }: MasterRunbookFormProps) {
    const { t } = useTranslation()
    const tasks = form.watch('tasks') || []
    const [localTasks, setLocalTasks] = useState<RunbookTask[]>(tasks)
    // Script generation is offered on an existing runbook because the generated set is
    // stored against its id. The task itself does not need saving first.
    const [genTaskNo, setGenTaskNo] = useState<number | null>(null)
    // A script for several consecutive manual tasks, asked for here (Andrew, 14 Oct 2026).
    const [taskScriptOpen, setTaskScriptOpen] = useState(false)
    const { toast } = useToast()
    const queryClient = useQueryClient()

    // Same two modes the object runbook offers. Off by default, matching objects: the
    // dialog's own Save is the expected way out, and writing to the server on every
    // keystroke would surprise anyone who opened the runbook to look rather than edit.
    // On, it persists tasks as you go — which is what you want when you are building a
    // task, generating its script, and do not intend to close the dialog for a while.
    const [autoUpdate, setAutoUpdate] = useState(false)

    // Which tasks already have a generated script — decides whether renumbering can do
    // any harm, and so whether the insert control needs guarding at all.
    const { data: codeTaskNos = [] } = useQuery<number[]>({
        queryKey: ['master-runbook-code-tasks', masterRunbookId],
        queryFn: async () => (await masterRunbooksApi.listTaskCode(masterRunbookId as number)).data?.data?.taskNos ?? [],
        enabled: masterRunbookId != null,
        staleTime: 60 * 1000,
    })

    const saveTasks = useMutation({
        mutationFn: (tasksToSave: RunbookTask[]) => {
            const values = form.getValues()
            // Technologies and GenerationContext are deliberately NOT sent: the endpoint
            // only overwrites them when they are present, and a task-level save has no
            // business clearing the inputs the runbook was generated from.
            return masterRunbooksApi.update(masterRunbookId as number, {
                description: values.description,
                information: values.information,
                tasks: tasksToSave,
            } as any)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['master-runbooks'] })
        },
        onError: (e: any) => toast({
            title: t('objects.runbook.saveFailed', 'Could not save the runbook'),
            description: e?.response?.data?.message || e?.message,
            variant: 'destructive',
        }),
    })

    // Form state follows every change, whichever save mode is on. Without this, turning
    // auto-update off would strand edits in the table and the dialog's Save would write
    // the tasks as they were when it opened.
    const handleTasksChange = useCallback((updated: RunbookTask[]) => {
        const tasksWithOffsets = calculateRunbookTimes(updated)
        form.setValue('tasks', tasksWithOffsets, { shouldValidate: false, shouldDirty: true })
        setLocalTasks(tasksWithOffsets)
    }, [form])

    // Persisting is the other channel: on every change while auto-update is on, or on the
    // toolbar's Save when it is off. Only possible once the runbook exists — on a new one
    // the dialog's Save is what creates it.
    const handleTasksSave = useCallback((updated: RunbookTask[]) => {
        const tasksWithOffsets = calculateRunbookTimes(updated)
        form.setValue('tasks', tasksWithOffsets, { shouldValidate: false, shouldDirty: true })
        setLocalTasks(tasksWithOffsets)
        // A blank name would be rejected by the endpoint, so mid-rename edits stay local
        // rather than firing a failed request per keystroke.
        if (masterRunbookId != null && (form.getValues().description || '').trim()) {
            saveTasks.mutate(tasksWithOffsets)
        }
    }, [masterRunbookId, form, saveTasks])

    // Sync localTasks with form state when tasks change
    // This ensures tasks are loaded when editing (when form.reset is called)
    useEffect(() => {
        const formTasks = form.getValues('tasks') || []
        // Update localTasks when form tasks change
        setLocalTasks(formTasks)
    }, [tasks, form]) // Sync when tasks from form.watch change

    return (
        <div className="space-y-6">
            <div className="max-w-2xl">
                <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>{t('masterRunbooks.name', 'Name')}</FormLabel>
                            <FormControl>
                                <Input
                                    placeholder={t('masterRunbooks.namePlaceholder', 'Enter master runbook name')}
                                    {...field}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </div>

            <div className="max-w-2xl">
                <FormField
                    control={form.control}
                    name="icon"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>{t('masterRunbooks.icon', 'Icon')}</FormLabel>
                            <FormControl>
                                <IconPicker value={field.value ?? null} onChange={field.onChange} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </div>

            <div className="max-w-2xl">
                <FormField
                    control={form.control}
                    name="information"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>{t('masterRunbooks.information')}</FormLabel>
                            <FormControl>
                                <RichTextEditor
                                    value={field.value || ''}
                                    onChange={field.onChange}
                                    placeholder={t('masterRunbooks.informationPlaceholder')}
                                    className="[&_.ProseMirror]:min-h-[120px]"
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </div>

            <div className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <FormLabel>{t('objects.runbook.tasks')}</FormLabel>
                    {masterRunbookId != null && (
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setTaskScriptOpen(true)}
                            disabled={localTasks.length === 0}
                        >
                            <FileCode className="h-4 w-4 mr-2" />{t('masterRunbooks.taskScript.open')}
                        </Button>
                    )}
                </div>
                <RunbookTable
                    tasks={localTasks}
                    onSave={handleTasksSave}
                    onTasksChange={handleTasksChange}
                    codeTaskNos={codeTaskNos}
                    taskCode={aiCode}
                    onFetchTaskCode={masterRunbookId != null
                        ? async (taskNo: number) => (await masterRunbooksApi.getTaskCode(masterRunbookId, taskNo)).data?.data?.set ?? null
                        : undefined}
                    autoUpdate={autoUpdate}
                    onAutoUpdateChange={setAutoUpdate}
                    isSaving={saveTasks.isPending}
                    // Toolbar shown only on an existing runbook: saving tasks to the server
                    // needs an id, and offering the control while creating one would promise
                    // something it cannot do.
                    hideToolbar={masterRunbookId == null}
                    suggestionMasterId={masterRunbookId}
                    onGenerateCode={masterRunbookId ? (taskNo) => setGenTaskNo(taskNo) : undefined}
                />
            </div>

            {masterRunbookId != null && taskScriptOpen && (
                <TaskScriptDialog
                    masterId={masterRunbookId}
                    tasks={localTasks}
                    open={taskScriptOpen}
                    onOpenChange={setTaskScriptOpen}
                />
            )}

            {masterRunbookId != null && genTaskNo != null && (
                <GenerateCodeDialog
                    masterId={masterRunbookId}
                    taskNo={genTaskNo}
                    method={localTasks.find((t) => t.taskNo === genTaskNo)?.method || 'manual'}
                    task={localTasks.find((t) => t.taskNo === genTaskNo)?.task || ''}
                    commands={localTasks.find((t) => t.taskNo === genTaskNo)?.commands || ''}
                    open={genTaskNo != null}
                    onOpenChange={(o) => { if (!o) setGenTaskNo(null) }}
                />
            )}
        </div>
    )
}
